import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { Box } from "@mui/material";
import { MyHrAllChangeRequestsCard } from "my-hr/components";
import { useMyHrUsersWithChangeRequest } from "my-hr/hooks";
import { OcPageNameTitle, OcPageTransition } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcClientState,
  useOcTableState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcRoleFilters,
} from "oc/hooks";

export const MyHRUserRequestsPage = () => {
  const history = useHistory();
  const isMobile = useOcIsMobile();
  const { t } = useTranslation();

  let { contractCode: contract } = useOcCurrentUserState();

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract: contract,
    product: "MyHR",
    role: "examiner",
  });

  const [onlyRequested, setOnlyRequested] = useOcClientState<boolean>(
    "showOnlyRequestedChangeRequests",
    true,
  );
  const [prevOnlyRequested, setPrevOnlyRequested] = useOcClientState<boolean>(
    "prevShowOnlyRequestedChangeRequests",
    true,
  );

  let tableId = "allChangeRequestsTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });

  const handleCompleted = () => {
    //console.log("completed");
  };

  const { data, loading, loadQuery, requestsCount, allUserCount } =
    useMyHrUsersWithChangeRequest({
      onlyRequested,
      roleFilters,
      tableData,
      onCompleted: handleCompleted,
    });

  useEffect(() => {
    loadQuery();
    setPrevRoleFilters(roleFilters);
  }, []); // eslint-disable-line

  const buttonDisable =
    _.isEqual(roleFilters, prevRoleFilters) &&
    onlyRequested === prevOnlyRequested;

  type UserWithChangeRequest = {
    _id: string;
    name: string;
    employee: string;
    // add other fields as needed
  };

  const filteredData = data?.filter((item: UserWithChangeRequest) => {
    if (tableData?.searchText) {
      return item.name
        .toLowerCase()
        .includes(tableData?.searchText.toLowerCase());
    }
    return true;
  });

  const sortDirection: "asc" | "desc" =
    tableData.sortDirection === "asc" ? "asc" : "desc";
  const sortedArray = _.orderBy(
    filteredData,
    tableData.sortField,
    sortDirection,
  );

  const total = filteredData?.length || 0;

  const list = {
    page: tableData.page,
    pageSize: tableData.pageSize,
    rows: sortedArray,
    total: total,
    totalPages: Math.floor(
      (total + tableData.pageSize - 1) / tableData.pageSize,
    ),
  };

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const originalonSelectRow = (o: any) => {
    if (isMobile) {
      history.push(`/my-hr/view-request/${o?.row?.employee}`);
    } else {
      history.push(`/my-hr/view-request/${o?.employee}`);
    }
  };

  const onSelectRow = (o: UserWithChangeRequest) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? null : o?._id);
    } else {
      originalonSelectRow(o);
    }
  };

  const handleOnlyReqestedChange = (state: boolean) => {
    setOnlyRequested(state);
  };

  const handleTableDataChange = (state: typeof initialTableData) => {
    setTableData(state);
  };

  const handleLoadQuery = () => {
    loadQuery();
    setPrevRoleFilters(roleFilters);
    setPrevOnlyRequested(onlyRequested);
  };

  const handleClearFilters = () => {
    resetRoleFilters();
  };

  return (
    <OcPageTransition>
      <OcPageNameTitle title={t("MY_HR_CHANGE_REQUESTS")} goBack={false} />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        <MyHrAllChangeRequestsCard
          buttonDisable={buttonDisable}
          onAddFilter={handleAddFilter}
          onRemoveFilter={handleRemoveFilter}
          onOnlyRequestedChange={handleOnlyReqestedChange}
          data={list.rows}
          roleFilters={roleFilters}
          loading={loading && !data.rows}
          tableData={tableData}
          onTableDataChange={handleTableDataChange}
          onSelectRow={onSelectRow}
          tableId={tableId}
          contract={contract}
          onLoadQuery={handleLoadQuery}
          onClearFilters={handleClearFilters}
          requestsCount={requestsCount}
          allUserCount={allUserCount}
          selectedRowId={selectedRowId}
          handleMoreButton={originalonSelectRow}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyHRUserRequestsPage;
